import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Star } from 'lucide-react';
import { trackPhoneClick } from '../utils/analytics';

/**
 * HeroMinimal — clean premium editorial hero.
 * Design principles per client brief:
 *   - Less text. More impact.
 *   - Clear headline + tagline signature
 *   - ONE primary CTA (Book Now), one secondary (call)
 *   - Real airport photograph as backdrop (parallax scroll)
 */
export const HeroMinimal = () => {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToBooking = () => {
    const el = document.getElementById('booking');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      className="relative flex min-h-[95vh] items-center justify-start overflow-hidden bg-black"
      data-testid="hero-minimal"
    >
      {/* Backdrop image with subtle parallax */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{ transform: `translateY(${scrollY * 0.25}px)` }}
      >
        <img
          src="/vehicles/e5008-airport-front.jpg"
          alt="Peugeot E-5008 GT at Porto Airport — GO VIP Portugal"
          className="h-full w-full object-cover object-center"
          fetchPriority="high"
        />
      </div>

      {/* Left-to-right dark gradient for text legibility */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.1) 100%)',
        }}
      />
      {/* Bottom fade */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent"
      />

      <div className="relative z-10 container mx-auto max-w-6xl px-6 md:px-10 pt-24">
        {/* Small rating chip */}
        <div
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-1.5 text-[11px] font-medium tracking-widest text-amber-300 backdrop-blur-md"
          data-testid="hero-rating"
        >
          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
          5.0 · GOOGLE VERIFIED
        </div>

        {/* Main headline */}
        <h1
          className="mb-5 font-serif font-bold leading-[1.02] tracking-tight text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.25rem]"
          data-testid="hero-headline"
        >
          {t('hero_v2.line1')}
          <br />
          <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent">
            {t('hero_v2.line2')}
          </span>
        </h1>

        {/* Slogan / signature */}
        <p
          className="mb-10 max-w-lg font-serif italic text-lg text-zinc-300/95 md:text-xl"
          data-testid="hero-tagline"
        >
          {t('hero_v2.tagline')}
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={scrollToBooking}
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-black shadow-[0_20px_40px_-15px_rgba(245,158,11,0.6)] hover:from-amber-400 hover:to-amber-500 transition-all"
            data-testid="hero-book-cta"
          >
            {t('hero_v2.book')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>

          <a
            href="tel:+351932651940"
            onClick={() => trackPhoneClick('hero_minimal')}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/[0.06] px-6 py-4 text-sm font-medium text-white backdrop-blur-md hover:bg-white/15 transition-all"
            data-testid="hero-call-cta"
          >
            <span className="text-amber-300">+351</span> 932 651 940
          </a>
        </div>
      </div>

      {/* Decorative bottom-right service tags */}
      <div className="pointer-events-none absolute bottom-8 right-6 z-10 hidden text-right md:block">
        <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.3em] text-amber-300/80">
          {t('hero_v2.serving')}
        </p>
        <p className="text-sm font-medium text-white/70">
          Porto · Lisbon · Douro · Northern Portugal
        </p>
      </div>
    </section>
  );
};

export default HeroMinimal;
